import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/sections/Footer";
import { COMPANY, SUPPORT_EMAIL } from "@/lib/company";
import { useI18n } from "@/i18n";
import { SeoHead } from "@/i18n/Seo";
import { RichText } from "@/components/RichText";
import { LanguageSelector } from "@/components/LanguageSelector";

// Renders the structured legal content blocks (paragraphs, lists, cookie table).
function Blocks({ blocks }) {
  return (
    <>
      {(blocks || []).map((b, i) => {
        if (b.p) return <p key={i} data-testid={b.testid}><RichText text={b.p} /></p>;
        if (b.ul) {
          return (
            <ul key={i} className="list-disc space-y-1.5 pl-6">
              {b.ul.map((item, j) => <li key={j}><RichText text={item} /></li>)}
            </ul>
          );
        }
        if (b.table) {
          return (
            <div key={i} className="overflow-x-auto" data-testid="legal-cookie-table">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr>
                    {b.table.head.map((h) => (
                      <th key={h} className="border-b border-re-line px-3 py-2 font-semibold text-re-ink">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.table.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td key={ci} className="border-b border-re-line/60 px-3 py-2 align-top">
                          {ci === 0 ? <code className="text-re-red">{cell}</code> : cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      })}
    </>
  );
}

// Shared layout for all legal pages: logo bar → prose → shared footer.
// `content` is the per-language structured page object from the dictionaries.
export function LegalLayout({ page, path, content, testid }) {
  const { t } = useI18n();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white" data-testid={testid}>
      <SeoHead page={page} path={path} />
      <header className="border-b border-re-line">
        <div className="container-re flex items-center justify-between py-5">
          <Link to="/" data-testid="legal-logo-home"><Logo /></Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Link to="/" className="text-sm font-semibold text-re-muted transition-colors hover:text-re-charcoal" data-testid="legal-back-home">
              {t("legalUi.back")}
            </Link>
          </div>
        </div>
      </header>

      <main className="container-re py-14 md:py-20">
        <div className="mx-auto max-w-3xl">
          <h1 className="font-heading text-3xl font-black tracking-tight text-re-ink md:text-4xl" data-testid="legal-title">{content.title}</h1>
          <p className="mt-3 text-sm text-re-muted">{t("legalUi.updated")}</p>
          <div className="legal-prose mt-8 space-y-6 text-[15px] leading-relaxed text-re-charcoal">
            <Blocks blocks={content.intro} />
            {(content.sections || []).map((s, i) => (
              <section key={i} className="space-y-2">
                <h2 className="font-heading text-lg font-bold text-re-ink">{s.n ? `${s.n}. ` : ""}{s.title}</h2>
                <div className="space-y-2 text-re-charcoal/90"><Blocks blocks={s.blocks} /></div>
              </section>
            ))}
          </div>

          {/* Shared provider details — ensures every legal page shows the full identity + address. */}
          <div className="mt-12 rounded-2xl border border-re-line bg-re-soft/40 p-5 text-sm text-re-muted" data-testid="legal-provider-details">
            <p className="font-semibold text-re-charcoal">{COMPANY.brand}</p>
            <p>{t("legalUi.opBy")} {COMPANY.operator}</p>
            <p>{t("legalUi.entNo")} {COMPANY.enterpriseNumber} · {t("legalUi.vat")} {COMPANY.vat}</p>
            <p>{COMPANY.address.line1}, {COMPANY.address.postalCode} {COMPANY.address.city}, {COMPANY.address.country}</p>
            <p>
              <a className="text-re-red hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
